// hooks/useTypewriterPaste.js
import { useCallback } from 'react';

/**
 * Core typewriter animation — shared by the onPaste hook and any manual caller
 * (e.g. a Paste button that reads the clipboard via navigator.clipboard).
 */
export function typewriterType(setValue, text, options = {}) {
  const {
    baseSpeed = 10,      // base ms per character
    variance = 11,      // random extra delay (feels natural)
    onComplete = null,  // callback fired once the final char is set
  } = options;

  if (!text) return;

  setValue(''); // clear instantly

  let index = 0;

  const typeNextChar = () => {
    if (index < text.length) {
      setValue((prev) => prev + text[index]);
      index++;
      const delay = baseSpeed + Math.random() * variance;
      setTimeout(typeNextChar, delay);
    } else if (onComplete) {
      onComplete(text);
    }
  };

  typeNextChar();
}

/**
 * Typewriter-style paste animation hook
 * When user pastes into an input, it clears and types out the text character-by-character
 */
export function useTypewriterPaste(setValue, options = {}) {
  const { baseSpeed, variance, onComplete } = options;

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const pastedText = (e.clipboardData || window.clipboardData).getData('text').trim();
    typewriterType(setValue, pastedText, { baseSpeed, variance, onComplete });
  }, [setValue, baseSpeed, variance, onComplete]);

  return handlePaste;
}